import { type z } from 'zod';

/**
 * Converts a Zod schema to a validation function compatible with Radix Form's match prop.
 * The returned function takes the current value and optionally the entire FormData.
 *
 * @param schema - Zod schema to validate against
 * @returns Validation function that returns true if valid, false if invalid
 */
export function zodToMatch<T>(
  schema: z.ZodSchema<T>
): (value: unknown, formData?: FormData) => boolean {
  return (value: unknown) => {
    const result = schema.safeParse(value);
    return result.success;
  };
}

/**
 * Extracts the first error message from a Zod validation result.
 * Useful for displaying custom error messages based on Zod validation.
 *
 * @param schema - Zod schema to validate against
 * @param value - Value to validate
 * @returns Error message string if invalid, undefined if valid
 */
export function getZodErrorMessage<T>(schema: z.ZodSchema<T>, value: unknown): string | undefined {
  const result = schema.safeParse(value);
  if (result.success) return undefined;

  const firstError = result.error.issues[0];
  return firstError?.message;
}

/**
 * Creates a match function that validates against a specific Zod error type.
 * This allows for more granular validation messages.
 *
 * @param schema - Zod schema to validate against
 * @param errorType - Specific Zod error type to match (e.g., 'too_small', 'invalid_string')
 * @returns Validation function that returns true if the specific error is present
 */
export function zodErrorMatch<T>(
  schema: z.ZodSchema<T>,
  errorType: string
): (value: unknown, formData?: FormData) => boolean {
  return (value: unknown) => {
    const result = schema.safeParse(value);
    if (result.success) return false;

    return result.error.issues.some(error => error.code === errorType);
  };
}
